import { useCallback, useEffect, useState } from "react";
import { useI18n } from "../context/I18nContext";
import { useAuth } from "../context/AuthContext";
import {
  addUserCompany,
  deleteUser,
  inviteUser,
  listAllCompanies,
  listUserCompanies,
  listUsers,
  removeUserCompany,
  updateUser,
} from "../lib/api";
import { toast } from "sonner";
import { Building2, Pencil, Plus, Shield, Trash2, UserPlus, X } from "lucide-react";

export default function Users() {
  const { t } = useI18n();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin" || user?.is_super_admin;
  const isSuperAdmin = !!user?.is_super_admin;
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInvite, setShowInvite] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [managingUser, setManagingUser] = useState(null);
  const [form, setForm] = useState({ email: "", name: "", role: "member", password: "" });

  const refresh = useCallback(async () => {
    try { const d = await listUsers(); setUsers(d.users || []); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { refresh(); }, [refresh]);

  const onInvite = async (e) => {
    e.preventDefault();
    try {
      await inviteUser(form);
      toast.success(t("success"));
      setShowInvite(false);
      setForm({ email: "", name: "", role: "member", password: "" });
      await refresh();
    } catch (err) { toast.error(err.response?.data?.detail || err.message); }
  };

  const onDelete = async (id) => {
    if (!window.confirm(t("confirm_delete"))) return;
    try { await deleteUser(id); await refresh(); toast.success(t("success")); }
    catch (err) { toast.error(err.response?.data?.detail || err.message); }
  };

  if (loading) return <div className="px-12 py-12 text-sm text-zinc-500">{t("loading")}</div>;

  return (
    <div className="px-8 lg:px-12 py-10 max-w-4xl" data-testid="users-page">
      <div className="flex items-end justify-between">
        <div>
          <div className="cs-label">team</div>
          <h1 className="font-heading text-4xl font-bold tracking-tight mt-2">{t("user_management")}</h1>
        </div>
        {isAdmin && <button onClick={() => setShowInvite(true)} className="inline-flex items-center gap-2 bg-cs-accent text-white px-3.5 py-2 rounded-md text-sm font-semibold hover:bg-cs-accentHover" data-testid="invite-btn"><UserPlus size={14} /> {t("invite_user")}</button>}
      </div>

      <div className="bg-white border border-zinc-200 rounded-md mt-8 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-zinc-50">
            <tr className="text-left">
              <th className="cs-label px-5 py-3">{t("name")}</th>
              <th className="cs-label px-5 py-3">{t("email")}</th>
              <th className="cs-label px-5 py-3">{t("role")}</th>
              <th className="cs-label px-5 py-3 font-mono">empresas</th>
              <th className="px-5 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} className="border-t border-zinc-100" data-testid={`user-row-${u.id}`}>
                <td className="px-5 py-3 font-medium text-zinc-900">{u.name}</td>
                <td className="px-5 py-3 font-mono text-xs">{u.email}</td>
                <td className="px-5 py-3"><span className="text-xs font-mono uppercase text-cs-accent">{u.role}</span></td>
                <td className="px-5 py-3 font-mono text-[11px] text-zinc-500">{(u.company_ids || []).length}</td>
                <td className="px-5 py-3 text-right whitespace-nowrap">
                  {(isAdmin || u.email === user?.email) && (
                    <button onClick={() => setEditingUser(u)} className="p-1.5 rounded hover:bg-zinc-200 text-zinc-500 hover:text-cs-accent mr-1" title="Editar usuário" data-testid={`user-edit-${u.id}`}>
                      <Pencil size={14} />
                    </button>
                  )}
                  {isAdmin && (
                    <button onClick={() => setManagingUser(u)} className="p-1.5 rounded hover:bg-zinc-200 text-zinc-500 hover:text-cs-accent mr-1" title="Gerenciar empresas" data-testid={`user-manage-companies-${u.id}`}>
                      <Building2 size={14} />
                    </button>
                  )}
                  {isAdmin && u.email !== user?.email && (
                    <button onClick={() => onDelete(u.id)} className="p-1.5 rounded hover:bg-zinc-200 text-zinc-500 hover:text-red-600" data-testid={`user-delete-${u.id}`}>
                      <Trash2 size={14} />
                    </button>
                  )}
                </td>
              </tr>
            ))}
            {users.length === 0 && (
              <tr><td colSpan={5} className="px-5 py-8 text-center text-zinc-500 text-sm">—</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {showInvite && (
        <div className="fixed inset-0 bg-zinc-950/40 z-50 flex items-center justify-center p-4" data-testid="invite-modal">
          <div className="bg-white border border-zinc-200 rounded-md shadow-2xl w-full max-w-md">
            <div className="px-6 py-4 border-b border-zinc-200">
              <h2 className="font-heading text-lg font-semibold">{t("invite_user")}</h2>
            </div>
            <form onSubmit={onInvite} className="p-6 space-y-3">
              <div>
                <label className="cs-label block mb-1.5">{t("name")}</label>
                <input type="text" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full px-3 py-2 border border-zinc-300 rounded-md text-sm" data-testid="invite-name-input" />
              </div>
              <div>
                <label className="cs-label block mb-1.5">{t("email")}</label>
                <input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full px-3 py-2 border border-zinc-300 rounded-md text-sm font-mono" data-testid="invite-email-input" />
              </div>
              <div>
                <label className="cs-label block mb-1.5">{t("role")}</label>
                <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="w-full px-3 py-2 border border-zinc-300 rounded-md text-sm" data-testid="invite-role-input">
                  <option value="member">{t("member")}</option>
                  <option value="admin">{t("admin")}</option>
                </select>
              </div>
              <div>
                <label className="cs-label block mb-1.5">{t("password")}</label>
                <input type="text" required minLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="senha temporária" className="w-full px-3 py-2 border border-zinc-300 rounded-md text-sm font-mono" data-testid="invite-password-input" />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button type="button" onClick={() => setShowInvite(false)} className="px-4 py-2 text-sm text-zinc-700 hover:bg-zinc-100 rounded-md" data-testid="invite-cancel-btn">{t("cancel")}</button>
                <button type="submit" className="px-4 py-2 bg-cs-accent text-white text-sm font-semibold rounded-md hover:bg-cs-accentHover" data-testid="invite-submit-btn">{t("create")}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {managingUser && (
        <ManageCompaniesModal
          target={managingUser}
          isSuperAdmin={isSuperAdmin}
          currentUserCompanyId={user?.company_id}
          onClose={() => setManagingUser(null)}
          onChanged={refresh}
        />
      )}

      {editingUser && (
        <EditUserModal
          target={editingUser}
          canChangeRole={isAdmin && editingUser.email !== user?.email}
          onClose={() => setEditingUser(null)}
          onSaved={refresh}
        />
      )}
    </div>
  );
}

function EditUserModal({ target, canChangeRole, onClose, onSaved }) {
  const [name, setName] = useState(target.name || "");
  const [email, setEmail] = useState(target.email || "");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [role, setRole] = useState(target.role || "member");
  const [saving, setSaving] = useState(false);

  const onSave = async (e) => {
    e?.preventDefault();
    if (password && password !== confirm) {
      toast.error("A confirmação de senha não confere");
      return;
    }
    setSaving(true);
    try {
      const payload = {};
      if (name.trim() && name !== target.name) payload.name = name.trim();
      if (email.trim() && email !== target.email) payload.email = email.trim();
      if (password) payload.password = password;
      if (canChangeRole && role !== target.role) payload.role = role;
      if (Object.keys(payload).length === 0) { toast.info("Nada para salvar"); onClose(); return; }
      await updateUser(target.id, payload);
      toast.success("Usuário atualizado");
      onSaved?.();
      onClose();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-zinc-950/50 z-50 flex items-center justify-center p-4" data-testid="edit-user-modal">
      <div className="bg-white border border-zinc-200 rounded-md shadow-2xl w-full max-w-md">
        <div className="px-6 py-4 border-b border-zinc-200 flex items-center justify-between">
          <div>
            <div className="cs-label">editar usuário</div>
            <h2 className="font-heading text-lg font-semibold mt-1">{target.name || target.email}</h2>
          </div>
          <button onClick={onClose} className="p-1.5 rounded hover:bg-zinc-100" data-testid="edit-user-close"><X size={16} /></button>
        </div>
        <form onSubmit={onSave} className="p-6 space-y-3">
          <div>
            <label className="cs-label !text-[10px] block mb-1.5">nome</label>
            <input type="text" value={name} onChange={(e) => setName(e.target.value)} className="w-full px-3 py-2 border border-zinc-300 rounded-md text-sm" data-testid="edit-user-name" />
          </div>
          <div>
            <label className="cs-label !text-[10px] block mb-1.5">email</label>
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="w-full px-3 py-2 border border-zinc-300 rounded-md text-sm font-mono" data-testid="edit-user-email" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="cs-label !text-[10px] block mb-1.5">nova senha (opcional)</label>
              <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" minLength={6} className="w-full px-3 py-2 border border-zinc-300 rounded-md text-sm font-mono" data-testid="edit-user-password" />
            </div>
            <div>
              <label className="cs-label !text-[10px] block mb-1.5">confirmar</label>
              <input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="••••••••" minLength={6} disabled={!password} className="w-full px-3 py-2 border border-zinc-300 rounded-md text-sm font-mono disabled:bg-zinc-50" data-testid="edit-user-password-confirm" />
            </div>
          </div>
          {canChangeRole && (
            <div>
              <label className="cs-label !text-[10px] block mb-1.5">papel na empresa</label>
              <select value={role} onChange={(e) => setRole(e.target.value)} className="w-full px-3 py-2 border border-zinc-300 rounded-md text-sm" data-testid="edit-user-role">
                <option value="member">member</option>
                <option value="admin">admin</option>
              </select>
            </div>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-zinc-700 hover:bg-zinc-100 rounded-md" data-testid="edit-user-cancel">Cancelar</button>
            <button type="submit" disabled={saving} className="px-4 py-2 bg-cs-accent text-white text-sm font-semibold rounded-md hover:bg-cs-accentHover disabled:opacity-60" data-testid="edit-user-save">{saving ? "Salvando..." : "Salvar"}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function ManageCompaniesModal({ target, isSuperAdmin, currentUserCompanyId, onClose, onChanged }) {
  const [links, setLinks] = useState([]);
  const [allCompanies, setAllCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [newCompanyId, setNewCompanyId] = useState("");
  const [newRole, setNewRole] = useState("member");

  const load = async () => {
    setLoading(true);
    try {
      const d = await listUserCompanies(target.id);
      setLinks(d.companies || []);
      if (isSuperAdmin) {
        const all = await listAllCompanies();
        setAllCompanies(all.companies || []);
      }
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const onAdd = async () => {
    if (!newCompanyId) return;
    setAdding(true);
    try {
      await addUserCompany(target.id, { company_id: newCompanyId, role: newRole });
      toast.success("Vínculo adicionado");
      setNewCompanyId("");
      setNewRole("member");
      await load();
      onChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    finally { setAdding(false); }
  };

  const onRemove = async (cid) => {
    if (!window.confirm("Remover este vínculo?")) return;
    try {
      await removeUserCompany(target.id, cid);
      toast.success("Vínculo removido");
      await load();
      onChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };

  const availableToAdd = allCompanies.filter((c) => !links.some((l) => l.id === c.id));

  return (
    <div className="fixed inset-0 bg-zinc-950/50 z-50 flex items-center justify-center p-4" data-testid="manage-companies-modal">
      <div className="bg-white border border-zinc-200 rounded-md shadow-2xl w-full max-w-lg flex flex-col max-h-[85vh]">
        <div className="px-6 py-4 border-b border-zinc-200 flex items-center justify-between">
          <div>
            <div className="cs-label">multi-empresa</div>
            <h2 className="font-heading text-lg font-semibold mt-1">Empresas de {target.name || target.email}</h2>
          </div>
          <button onClick={onClose} className="p-1.5 rounded hover:bg-zinc-100" data-testid="manage-companies-close"><X size={16} /></button>
        </div>
        <div className="p-6 overflow-y-auto flex-1 scrollbar-thin space-y-4">
          {loading ? (
            <div className="text-xs text-zinc-500">Carregando…</div>
          ) : (
            <>
              <div className="space-y-2">
                {links.map((l) => (
                  <div key={l.id} className="flex items-center gap-3 px-3 py-2 border border-zinc-200 rounded-md" data-testid={`link-row-${l.id}`}>
                    {l.role === "admin" ? <Shield size={14} className="text-cs-accent" /> : <Building2 size={14} className="text-zinc-500" />}
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-zinc-900 truncate">{l.name}</div>
                      <div className="font-mono text-[10px] uppercase text-zinc-500 mt-0.5">{l.role}</div>
                    </div>
                    {(isSuperAdmin || l.id === currentUserCompanyId) && links.length > 1 && (
                      <button onClick={() => onRemove(l.id)} className="p-1.5 rounded text-zinc-400 hover:bg-zinc-100 hover:text-red-600" data-testid={`link-remove-${l.id}`}>
                        <Trash2 size={12} />
                      </button>
                    )}
                  </div>
                ))}
                {links.length === 0 && <div className="text-xs text-zinc-500 px-3 py-3 border border-dashed border-zinc-200 rounded-md">Nenhum vínculo encontrado.</div>}
              </div>

              {/* Add new link — only super-admin (only they can list ALL companies in the picker) */}
              {isSuperAdmin && (
                <div className="border-t border-zinc-200 pt-4">
                  <div className="cs-label mb-2">Adicionar empresa</div>
                  <div className="flex items-center gap-2">
                    <select value={newCompanyId} onChange={(e) => setNewCompanyId(e.target.value)} className="flex-1 px-3 py-2 border border-zinc-300 rounded-md text-xs" data-testid="add-link-company-select">
                      <option value="">Selecione…</option>
                      {availableToAdd.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                    <select value={newRole} onChange={(e) => setNewRole(e.target.value)} className="px-3 py-2 border border-zinc-300 rounded-md text-xs" data-testid="add-link-role-select">
                      <option value="member">member</option>
                      <option value="admin">admin</option>
                    </select>
                    <button onClick={onAdd} disabled={!newCompanyId || adding} className="inline-flex items-center gap-1 bg-cs-accent text-white px-3 py-2 rounded-md text-xs font-semibold hover:bg-cs-accentHover disabled:opacity-60" data-testid="add-link-submit">
                      <Plus size={12} /> {adding ? "..." : "Adicionar"}
                    </button>
                  </div>
                </div>
              )}
              {!isSuperAdmin && (
                <p className="text-[11px] text-zinc-500 pt-2 border-t border-zinc-200">
                  Apenas super-admins podem adicionar novos vínculos. Como admin desta empresa você pode remover o usuário daqui (será preservado nas outras empresas dele).
                </p>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
